import { type Edge, type Node, Position } from '@xyflow/react'

import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'
import {
  ActivityNode,
  EventNodes,
  NodeTypesWithReadonly,
  NodeTypesWithSystemDefinedBoundaryEvents,
} from '@flowx/designer/shared/entities/node.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'

import {
  AiDraftPreviewRender,
  NavAreaRender,
  SequenceConnectionPayload,
  SequenceCreatePayload,
  SequenceCreateSource,
  SequenceValidationError,
  SequenceValidationResult,
} from './bpmn-diagram.types'
import {
  computeStepPolyline,
  isOrthogonalRoute,
  routeBetweenRectPositions,
} from './edges/orthogonal-edge.util'
import { getNodeDimensions } from './node-visuals'
import { TARGET_HANDLE_POSITIONS } from './target-handles'

import { BoundaryEventRender, CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'
import { NodeAuditStyles } from '../../bpmn-graph.constants'
import { Point } from '../../dictionary/geometry.types'

export const SWIMLANE_NODE_TYPE = 'swimlane'
export const BPMN_NODE_TYPE = 'bpmnNode'
export const BOUNDARY_EVENT_NODE_TYPE = 'boundaryEvent'
export const NAV_AREA_NODE_TYPE = 'navArea'
export const AI_PREVIEW_OUTLINE_NODE_TYPE = 'aiPreviewOutline'
export const SEQUENCE_EDGE_TYPE = 'sequence'

export const toLaneId = (id: SwimlaneSummary['id']): string => `lane-${id}`
export const toNodeId = (id: NodeDefinitionRender['id']): string => `node-${id}`
export const toBoundaryId = (id: BoundaryEventRender['id']): string => `boundary-${id}`
export const toSequenceId = (id: SequencePoints['id']): string => `sequence-${id}`

export interface SwimlaneNodeData extends Record<string, unknown> {
  swimlane: SwimlaneSummary
}

export interface BpmnNodeData extends Record<string, unknown> {
  definition: NodeDefinitionRender
  highlighted: boolean
  readonly: boolean
  /** Eye-slash marker: allowReadOnly version + node not yet flagged allowReadOnly */
  showReadonlyIcon: boolean
  /** Pre-existing node dimmed during the AI draft preview */
  disabled: boolean
  /**
   * Error marker. Set while a drag hovers an invalid drop spot, and kept
   * on a pasted node that came down on top of another one — paste centres the
   * clipboard on the click point without a validity check, so it is the only way
   * nodes end up overlapping. Clears once the node is moved somewhere free.
   */
  invalid?: boolean
  /**
   * Process-instance audit-state palette (visited/current/pending), read from the
   * definition's flxStyles. When set, the RF renderer overrides the state ladder
   * with this palette.
   */
  auditStyles?: NodeAuditStyles
  /**
   * BPMN cardinality, precomputed so the node can hide its connect-source handle
   * rather than offer a drag that `validateSequenceConnection` would reject on
   * drop. False once a non-gateway node has its one outgoing sequence.
   */
  canAddOutgoingSequence: boolean
}

export interface BoundaryEventNodeData extends Record<string, unknown> {
  boundaryEvent: BoundaryEventRender
  readonly: boolean
  /** Instance audit-state palette (see BpmnNodeData.auditStyles) */
  auditStyles?: NodeAuditStyles
}

export interface SequenceEdgeData extends Record<string, unknown> {
  sequence: SequencePoints
  /**
   * The single effective polyline. During an interaction React Flow updates
   * this field locally; after commit it is hydrated from sequence.layoutOptions.
   */
  points: Point[]
  readonly: boolean
  /** AI draft preview mode: grey non-interactive polyline */
  preview: boolean
  /** Instance audit-state palette (see BpmnNodeData.auditStyles) — overrides the state ladder */
  auditStyles?: NodeAuditStyles
  /**
   * Absolute-canvas rects of the source and target nodes (source may be a
   * boundary event). Used by the edge to slide its endpoints along the border
   * so the arrow enters from whichever side matches the adjacent segment's
   * direction — the approach the React Flow editable-edge "Step" example uses for
   * its non-Handle endpoints.
   */
  sourceRect: Rect
  targetRect: Rect
}

export interface NavAreaNodeData extends Record<string, unknown> {
  area: NavAreaRender
}

const DEFAULT_LANE_SIZE = { width: 1200, height: 400 }

/** Backend entities can arrive with null layoutOptions (e.g. freshly created); default them. */
function laneLayout(swimlane: SwimlaneSummary): {
  x: number
  y: number
  width: number
  height: number
} {
  return { x: 0, y: 0, ...DEFAULT_LANE_SIZE, ...(swimlane.layoutOptions ?? {}) }
}

export function toFlowGraph(
  data: CanvasData,
  options: {
    readonly: boolean
    highlightedNodeId?: number
    allowReadOnly?: boolean
    aiDraftPreview?: AiDraftPreviewRender | null
    /** Nav-view readonly-consumer preview: extra ids to render dimmed */
    navDisabledNodeIds?: number[]
    /** Nodes created by a paste — the only ones that can land on top of another (see BpmnNodeData.invalid) */
    pastedNodeIds?: number[]
  }
): { nodes: Node[]; edges: Edge[] } {
  const disabledNodeIds = new Set([
    ...(options.aiDraftPreview?.disabledNodeIds ?? []),
    ...(options.navDisabledNodeIds ?? []),
  ])
  const pastedNodeIds = new Set(options.pastedNodeIds ?? [])
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, laneLayout(swimlane)]))

  const nodeAbsoluteRect = (definition: NodeDefinitionRender) => {
    const lane = lanesById.get(definition.swimlaneDefinitionId)

    return {
      x: (lane?.x ?? 0) + (definition.layoutOptions?.x ?? 0),
      y: (lane?.y ?? 0) + (definition.layoutOptions?.y ?? 0),
      ...getNodeDimensions(definition.type),
    }
  }

  const isPastedOntoAnotherNode = (definition: NodeDefinitionRender): boolean =>
    pastedNodeIds.has(definition.id) &&
    overlapsAnyNode(nodeAbsoluteRect(definition), new Set([definition.id]), data, getNodeDimensions)

  const laneNodes: Node<SwimlaneNodeData>[] = data.swimlanes.map((swimlane) => {
    const layout = laneLayout(swimlane)

    return {
      id: toLaneId(swimlane.id),
      type: SWIMLANE_NODE_TYPE,
      position: { x: layout.x, y: layout.y },
      data: { swimlane: { ...swimlane, layoutOptions: layout } },
      draggable: false,
      selectable: false,
      focusable: false,
      // Lets pane panning / palette drops go through empty lane surface
      style: {
        width: layout.width,
        height: layout.height,
        pointerEvents: 'none' as const,
      },
      zIndex: 0,
    }
  })

  const bpmnNodes: Node<BpmnNodeData>[] = data.nodes.map((definition) => ({
    id: toNodeId(definition.id),
    type: BPMN_NODE_TYPE,
    parentId: toLaneId(definition.swimlaneDefinitionId),
    position: { x: definition.layoutOptions?.x ?? 0, y: definition.layoutOptions?.y ?? 0 },
    data: {
      definition,
      highlighted: definition.id === options.highlightedNodeId,
      readonly: options.readonly,
      // Per-node readonly marker: on allowReadOnly process versions,
      // user tasks / embedded subprocesses NOT yet flagged allowReadOnly show
      // the eye-slash icon (the pill menu's eye action toggles the flag).
      showReadonlyIcon:
        !!options.allowReadOnly &&
        NodeTypesWithReadonly.includes(definition.type as ActivityNode) &&
        !definition.allowReadOnly,
      disabled: disabledNodeIds.has(definition.id),
      invalid: isPastedOntoAnotherNode(definition),
      auditStyles: definition.flxStyles,
      canAddOutgoingSequence: canAddOutgoingSequence(
        { kind: 'node', node: definition },
        data.sequences
      ),
    },
    draggable: !options.readonly,
    zIndex: 10,
  }))

  const nodesById = new Map(data.nodes.map((node) => [node.id, node]))

  const boundaryNodes: Node<BoundaryEventNodeData>[] = data.boundaryEvents
    .filter((boundaryEvent) => nodesById.has(boundaryEvent.nodeDefinitionId))
    // Timer/message event nodes (start timer, intermediate catch/throw…) store their
    // schedule/config as a system-defined timer/message-event entity attached to the
    // node — those are not drawn as boundary events.
    .filter(
      (boundaryEvent) =>
        !NodeTypesWithSystemDefinedBoundaryEvents.includes(
          nodesById.get(boundaryEvent.nodeDefinitionId).type as EventNodes
        )
    )
    .map((boundaryEvent) => ({
      id: toBoundaryId(boundaryEvent.id),
      type: BOUNDARY_EVENT_NODE_TYPE,
      parentId: toNodeId(boundaryEvent.nodeDefinitionId),
      position: { x: boundaryEvent.layoutOptions?.x ?? 0, y: boundaryEvent.layoutOptions?.y ?? 0 },
      data: {
        boundaryEvent,
        readonly: options.readonly,
        auditStyles: boundaryEvent.flxStyles,
      },
      draggable: !options.readonly,
      zIndex: 20,
    }))

  const boundaryIds = new Set(data.boundaryEvents.map((boundaryEvent) => boundaryEvent.id))
  const boundariesById = new Map(
    data.boundaryEvents.map((boundaryEvent) => [boundaryEvent.id, boundaryEvent])
  )
  const sequenceSourceRect = (sequence: SequencePoints) => {
    if (!sequence.boundaryEventId) {
      return nodeAbsoluteRect(nodesById.get(sequence.startNodeId))
    }
    const boundaryEvent = boundariesById.get(sequence.boundaryEventId)
    const parentRect = nodeAbsoluteRect(nodesById.get(boundaryEvent.nodeDefinitionId))

    return {
      x: parentRect.x + (boundaryEvent.layoutOptions?.x ?? 0),
      y: parentRect.y + (boundaryEvent.layoutOptions?.y ?? 0),
      ...getNodeDimensions(boundaryEvent.type),
    }
  }

  const edges: Edge<SequenceEdgeData>[] = data.sequences
    .filter(
      (sequence) =>
        (sequence.boundaryEventId
          ? boundaryIds.has(sequence.boundaryEventId) &&
            nodesById.has(boundariesById.get(sequence.boundaryEventId).nodeDefinitionId)
          : nodesById.has(sequence.startNodeId)) && nodesById.has(sequence.endNodeId)
    )
    .map((sequence) => {
      const sourceRect = sequenceSourceRect(sequence)
      const targetRect = nodeAbsoluteRect(nodesById.get(sequence.endNodeId))
      const points = isOrthogonalRoute(sequence.layoutOptions)
        ? sequence.layoutOptions
        : initialSequencePoints(sourceRect, targetRect)

      return {
        id: toSequenceId(sequence.id),
        type: SEQUENCE_EDGE_TYPE,
        source: sequence.boundaryEventId
          ? toBoundaryId(sequence.boundaryEventId)
          : toNodeId(sequence.startNodeId),
        target: toNodeId(sequence.endNodeId),
        data: {
          sequence,
          points,
          readonly: options.readonly,
          preview: !!options.aiDraftPreview,
          auditStyles: sequence.flxStyles,
          sourceRect,
          targetRect,
        },
      }
    })

  // Dashed outline around the AI-generated content, behind the lanes
  const outline = options.aiDraftPreview?.outline
  const outlineNodes: Node[] = outline
    ? [
        {
          id: 'ai-preview-outline',
          type: AI_PREVIEW_OUTLINE_NODE_TYPE,
          position: { x: outline.x, y: outline.y },
          data: {},
          draggable: false,
          selectable: false,
          focusable: false,
          style: {
            width: outline.width,
            height: outline.height,
            pointerEvents: 'none' as const,
          },
          zIndex: -1,
        },
      ]
    : []

  return { nodes: [...outlineNodes, ...laneNodes, ...bpmnNodes, ...boundaryNodes], edges }
}

function rectEdgeCenter(rect: Rect, position: Position): Point {
  switch (position) {
    case Position.Left:
      return { x: rect.x, y: rect.y + rect.height / 2 }
    case Position.Right:
      return { x: rect.x + rect.width, y: rect.y + rect.height / 2 }
    case Position.Top:
      return { x: rect.x + rect.width / 2, y: rect.y }
    default:
      return { x: rect.x + rect.width / 2, y: rect.y + rect.height }
  }
}

/**
 * The port a pointer over (or beside) a node picks: the nearest of the node's
 * four connection points. Which of the four the pointer is closest to is the
 * whole rule — the same one for every shape family, since `rectEdgeCenter`
 * already resolves each family's ports to the same four points.
 */
export function nearestTargetPosition(rect: Rect, point: Point): Position {
  return TARGET_HANDLE_POSITIONS.map((position) => {
    const port = rectEdgeCenter(rect, position)

    return { position, distance: (port.x - point.x) ** 2 + (port.y - point.y) ** 2 }
  }).reduce((nearest, candidate) => (candidate.distance < nearest.distance ? candidate : nearest))
    .position
}

function sequenceRoutePositions(
  sourceRect: Rect,
  targetRect: Rect
): { source: Position; target: Position } {
  const sourceCenter = {
    x: sourceRect.x + sourceRect.width / 2,
    y: sourceRect.y + sourceRect.height / 2,
  }
  const targetCenter = {
    x: targetRect.x + targetRect.width / 2,
    y: targetRect.y + targetRect.height / 2,
  }
  const dx = targetCenter.x - sourceCenter.x
  const dy = targetCenter.y - sourceCenter.y

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { source: Position.Right, target: Position.Left }
      : { source: Position.Left, target: Position.Right }
  }

  return dy >= 0
    ? { source: Position.Bottom, target: Position.Top }
    : { source: Position.Top, target: Position.Bottom }
}

/** Initial route used by mapper hydration, connection preview and persistence. */
export function initialSequencePoints(
  sourceRect: Rect,
  targetRect: Rect,
  targetPosition?: Position
): Point[] {
  const positions = sequenceRoutePositions(sourceRect, targetRect)
  if (targetPosition && targetPosition !== positions.target) {
    return routeBetweenRectPositions(sourceRect, positions.source, targetRect, targetPosition)
  }

  return computeStepPolyline(
    rectEdgeCenter(sourceRect, positions.source),
    positions.source,
    rectEdgeCenter(targetRect, positions.target),
    positions.target
  )
}

/**
 * Resolves a React Flow connection (RF node ids) back to the BPMN entities it links.
 * Edge-building rules: the source is a node or a boundary event,
 * the target is always a node, and self-connections are rejected.
 */
export function resolveConnection(
  connection: { source: string; target: string },
  data: CanvasData
): SequenceConnectionPayload | null {
  if (connection.source === connection.target) {
    return null
  }

  const target = data.nodes.find((node) => toNodeId(node.id) === connection.target)
  const source = target ? resolveSequenceSource(connection.source, data) : null

  return source && target ? { source, target } : null
}

/**
 * End nodes terminate the flow — they accept incoming sequences but never
 * source one. End milestones are stage boundaries the flow continues past,
 * so they can.
 */
export function canStartSequence(type: NodeDefinitionRender['type']): boolean {
  return type !== EventNodes.END_EVENT
}

/** Resolves a connection's RF source id to the BPMN node or boundary event behind it */
export function resolveSequenceSource(
  sourceRfId: string,
  data: CanvasData
): SequenceCreateSource | null {
  const sourceNode = data.nodes.find((node) => toNodeId(node.id) === sourceRfId)
  if (sourceNode) {
    return canStartSequence(sourceNode.type) ? { kind: 'node', node: sourceNode } : null
  }

  const sourceBoundary = data.boundaryEvents.find(
    (boundaryEvent) => toBoundaryId(boundaryEvent.id) === sourceRfId
  )

  return sourceBoundary ? { kind: 'boundaryEvent', boundaryEvent: sourceBoundary } : null
}

/**
 * The backend rejects duplicate sequences with a 400 (sequence_definition.exists);
 * pre-checking locally turns that into an accurate message instead of a generic
 * "Failed to create sequence" toast after a failed request.
 */
export function connectionAlreadyExists(
  payload: SequenceConnectionPayload,
  sequences: CanvasData['sequences']
): boolean {
  const { source, target } = payload

  return sequences.some(
    (sequence) =>
      sequence.endNodeId === target.id &&
      (source.kind === 'boundaryEvent'
        ? sequence.boundaryEventId === source.boundaryEvent.id
        : sequence.startNodeId === source.node.id && !sequence.boundaryEventId)
  )
}

/**
 * BPMN cardinality: a non-gateway node may start at most one sequence, so the
 * flow leaving it stays unambiguous; gateways split as well as merge and are
 * exempt. Incoming sequences are never limited — any node can be reached from
 * several paths. Boundary events are unrestricted here — they already have a
 * natural single-outgoing shape and the rule targets nodes.
 */
export function canAddOutgoingSequence(
  source: SequenceCreateSource,
  sequences: CanvasData['sequences']
): boolean {
  if (source.kind !== 'node' || NodeEntity.isGatewayNode(source.node.type)) {
    return true
  }

  return !sequences.some(
    (sequence) => sequence.startNodeId === source.node.id && !sequence.boundaryEventId
  )
}

function sourceOwnerNodeId(source: SequenceCreateSource): number {
  return source.kind === 'node' ? source.node.id : source.boundaryEvent.nodeDefinitionId
}

function isRfConnectionCandidate(
  candidate: { source: string; target: string } | SequenceConnectionPayload
): candidate is { source: string; target: string } {
  return typeof candidate.source === 'string' && typeof candidate.target === 'string'
}

/**
 * Single BPMN connection policy used by hover feedback, drop-anywhere and the
 * host persistence guard. It returns a typed reason instead of losing domain
 * failures behind React Flow's boolean API.
 */
export function validateSequenceConnection(
  candidate: { source: string; target: string } | SequenceConnectionPayload,
  data: CanvasData
): SequenceValidationResult {
  let payload: SequenceConnectionPayload | null

  if (isRfConnectionCandidate(candidate)) {
    if (candidate.source === candidate.target) {
      return { valid: false, reason: 'self-connection' }
    }
    const target = data.nodes.find((node) => toNodeId(node.id) === candidate.target)
    if (!target) {
      return { valid: false, reason: 'invalid-target' }
    }
    const sourceNode = data.nodes.find((node) => toNodeId(node.id) === candidate.source)
    if (sourceNode && !canStartSequence(sourceNode.type)) {
      return { valid: false, reason: 'invalid-source' }
    }
    const source = resolveSequenceSource(candidate.source, data)
    if (!source) {
      return { valid: false, reason: 'invalid-source' }
    }
    payload = { source, target }
  } else {
    const target = data.nodes.find((node) => node.id === candidate.target.id)
    if (!target) {
      return { valid: false, reason: 'invalid-target' }
    }
    const candidateSource = candidate.source
    if (candidateSource.kind === 'node') {
      const sourceNode = data.nodes.find((node) => node.id === candidateSource.node.id)
      if (!sourceNode || !canStartSequence(sourceNode.type)) {
        return { valid: false, reason: 'invalid-source' }
      }
      payload = { source: { kind: 'node', node: sourceNode }, target }
    } else {
      const boundaryEvent = data.boundaryEvents.find(
        (boundary) => boundary.id === candidateSource.boundaryEvent.id
      )
      if (!boundaryEvent) {
        return { valid: false, reason: 'invalid-source' }
      }
      payload = { source: { kind: 'boundaryEvent', boundaryEvent }, target }
    }
  }

  if (sourceOwnerNodeId(payload.source) === payload.target.id) {
    return { valid: false, reason: 'self-connection' }
  }
  if (connectionAlreadyExists(payload, data.sequences)) {
    return { valid: false, reason: 'duplicate' }
  }
  if (!canAddOutgoingSequence(payload.source, data.sequences)) {
    return { valid: false, reason: 'outgoing-limit' }
  }

  return { valid: true, payload }
}

export function sequenceValidationMessage(reason: SequenceValidationError): string {
  switch (reason) {
    case 'self-connection':
      return 'A node cannot connect to itself'
    case 'invalid-source':
      return 'This node cannot start a sequence'
    case 'invalid-target':
      return 'A sequence must end at a node'
    case 'duplicate':
      return 'A sequence between these nodes already exists'
    case 'outgoing-limit':
      return 'This node already has an outgoing sequence'
  }
}

function absoluteNodeRect(node: NodeDefinitionRender, data: CanvasData): Rect | null {
  const lane = data.swimlanes.find((candidate) => candidate.id === node.swimlaneDefinitionId)
  if (!lane) {
    return null
  }

  return {
    ...toAbsolutePosition({ x: node.layoutOptions?.x ?? 0, y: node.layoutOptions?.y ?? 0 }, lane),
    ...getNodeDimensions(node.type),
  }
}

/** Add the exact custom-router points that the host must persist on creation. */
export function withInitialSequencePoints(
  payload: SequenceConnectionPayload,
  data: CanvasData,
  targetPosition?: Position
): SequenceCreatePayload | null {
  const targetRect = absoluteNodeRect(payload.target, data)
  if (!targetRect) {
    return null
  }

  let sourceRect: Rect | null = null
  if (payload.source.kind === 'node') {
    sourceRect = absoluteNodeRect(payload.source.node, data)
  } else {
    const boundary = payload.source.boundaryEvent
    const parent = data.nodes.find((node) => node.id === boundary.nodeDefinitionId)
    const parentRect = parent ? absoluteNodeRect(parent, data) : null
    if (parentRect) {
      sourceRect = {
        x: parentRect.x + (boundary.layoutOptions?.x ?? 0),
        y: parentRect.y + (boundary.layoutOptions?.y ?? 0),
        ...getNodeDimensions(boundary.type),
      }
    }
  }

  return sourceRect
    ? {
        ...payload,
        layoutOptions: initialSequencePoints(sourceRect, targetRect, targetPosition),
      }
    : null
}

export interface ConnectionTarget {
  /** React Flow id of the node the sequence would land on */
  rfId: string
  rect: Rect
  /** Which of the node's four connection points the pointer picked */
  position: Position
  valid: boolean
  /** Why the connection is rejected, when it is */
  reason?: SequenceValidationError
}

/**
 * The node a live connection gesture would land on, and the port on it.
 *
 * Resolved from the pointer alone — the same hit test the drop runs — so the
 * preview cannot promise a landing the drop will not honour. React Flow's own
 * closest-handle snapping is deliberately not consulted: it answers a different
 * question (which handle is within `connectionRadius`), and over a node body it
 * routinely picks that node's own connect-source handle, which strict mode then
 * rejects.
 */
export function resolveConnectionTarget(
  pointer: Point,
  sourceRfId: string,
  data: CanvasData
): ConnectionTarget | null {
  const node = findNodeAt(pointer, data, getNodeDimensions)
  const rect = node ? absoluteNodeRect(node, data) : null
  if (!node || !rect) {
    return null
  }
  const validation = validateSequenceConnection(
    { source: sourceRfId, target: toNodeId(node.id) },
    data
  )

  return {
    rfId: toNodeId(node.id),
    rect,
    position: nearestTargetPosition(rect, pointer),
    valid: validation.valid,
    reason: validation.valid === false ? validation.reason : undefined,
  }
}

/** BpmnGraphUtil.boundingBoxToSwimlaneLayoutOptions(40, 40, memberBounds) */
export const NAV_AREA_PADDING = 40

/**
 * Recomputes every nav-area rect from the CURRENT React Flow node positions —
 * the padded bounding box of its member nodes, same rule as
 * Area.decorateAreasWithLayoutOptions. Runs inside the drag's own setNodes pass
 * so the rect stays glued to the nodes (no Angular/effector round-trip).
 */
export function stretchNavAreaNodes(
  nodes: Node[],
  nodeDimensions: (type: NodeDefinitionRender['type']) => { width: number; height: number }
): Node[] {
  const memberRects = new Map<number, Rect[]>()
  nodes.forEach((node) => {
    if (node.type !== BPMN_NODE_TYPE) {
      return
    }
    const definition = (node.data as { definition: NodeDefinitionRender }).definition
    if (!definition.navigationAreaId) {
      return
    }
    const rects = memberRects.get(definition.navigationAreaId) ?? []
    rects.push({ ...node.position, ...nodeDimensions(definition.type) })
    memberRects.set(definition.navigationAreaId, rects)
  })

  return nodes.map((node) => {
    if (node.type !== NAV_AREA_NODE_TYPE) {
      return node
    }
    const area = (node.data as NavAreaNodeData).area
    const rects = (area.memberAreaIds ?? []).flatMap((leafId) => memberRects.get(leafId) ?? [])
    if (!rects.length) {
      return node
    }
    const x = Math.min(...rects.map((rect) => rect.x)) - NAV_AREA_PADDING
    const y = Math.min(...rects.map((rect) => rect.y)) - NAV_AREA_PADDING
    const layoutOptions = {
      x,
      y,
      width: Math.max(...rects.map((rect) => rect.x + rect.width)) + NAV_AREA_PADDING - x,
      height: Math.max(...rects.map((rect) => rect.y + rect.height)) + NAV_AREA_PADDING - y,
    }
    const current = area.layoutOptions
    if (
      current.x === layoutOptions.x &&
      current.y === layoutOptions.y &&
      current.width === layoutOptions.width &&
      current.height === layoutOptions.height
    ) {
      return node
    }

    return {
      ...node,
      position: { x: layoutOptions.x, y: layoutOptions.y },
      style: { ...node.style, width: layoutOptions.width, height: layoutOptions.height },
      data: { ...node.data, area: { ...area, layoutOptions } },
    }
  })
}

/**
 * Nav areas render as non-interactive children of the selected lane, between the
 * lane (z 0) and the BPMN nodes (z 10).
 * Parents come first in `navAreas` (host flattens the tree parent-first), so
 * nested areas paint above their containers.
 */
export function toNavAreaNodes(
  navAreas: NavAreaRender[],
  laneId: SwimlaneSummary['id'] | null
): Node<NavAreaNodeData>[] {
  if (!laneId) {
    return []
  }

  return navAreas
    .filter((area) => area.layoutOptions?.width > 0 && area.layoutOptions?.height > 0)
    .map((area) => ({
      id: `nav-area-${area.id}`,
      type: NAV_AREA_NODE_TYPE,
      parentId: toLaneId(laneId),
      position: { x: area.layoutOptions.x, y: area.layoutOptions.y },
      data: { area },
      draggable: false,
      selectable: false,
      focusable: false,
      style: {
        width: area.layoutOptions.width,
        height: area.layoutOptions.height,
        pointerEvents: 'none' as const,
      },
      zIndex: 5,
    }))
}

/**
 * Deepest nav area whose rect contains the (absolute) flow point. Nav-area
 * overlays are pointer-transparent, so clicks land on the pane and get
 * hit-tested here — `navAreas` is flattened parent-first, making the last hit
 * the innermost container.
 */
export function findNavAreaAt(
  point: Point,
  navAreas: NavAreaRender[],
  lane: SwimlaneSummary
): NavAreaRender | null {
  const laneOrigin = laneLayout(lane)
  const hits = navAreas.filter((area) => {
    const rect = area.layoutOptions
    const x = laneOrigin.x + rect.x
    const y = laneOrigin.y + rect.y

    return point.x >= x && point.x <= x + rect.width && point.y >= y && point.y <= y + rect.height
  })

  return hits.length ? hits[hits.length - 1] : null
}

export function toAbsolutePosition(relativePosition: Point, swimlane: SwimlaneSummary): Point {
  const layout = laneLayout(swimlane)

  return {
    x: layout.x + relativePosition.x,
    y: layout.y + relativePosition.y,
  }
}

export function toSwimlaneRelative(absolutePosition: Point, swimlane: SwimlaneSummary): Point {
  const layout = laneLayout(swimlane)

  return {
    x: absolutePosition.x - layout.x,
    y: absolutePosition.y - layout.y,
  }
}

/**
 * Absolute-coordinate node bounds that constrain a lane resize: the lane's own
 * nodes bound its top/bottom edges, while ALL
 * nodes bound left/right because x/width sync pool-wide on commit. Lanes without
 * nodes get ±Infinity bounds — clamps fall back to the minimum size.
 */
export interface LaneResizeBounds {
  ownMinY: number
  ownMaxY: number
  allMinX: number
  allMaxX: number
}

export function computeLaneResizeBounds(
  data: CanvasData,
  getNodeDimensions: (type: NodeDefinitionRender['type']) => { width: number; height: number }
): Map<SwimlaneSummary['id'], LaneResizeBounds> {
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))
  const nodeRects = data.nodes
    .filter((node) => lanesById.has(node.swimlaneDefinitionId))
    .map((node) => {
      const absolute = toAbsolutePosition(
        { x: node.layoutOptions?.x ?? 0, y: node.layoutOptions?.y ?? 0 },
        lanesById.get(node.swimlaneDefinitionId)
      )

      return { ...absolute, ...getNodeDimensions(node.type), laneId: node.swimlaneDefinitionId }
    })

  const allMinX = Math.min(...nodeRects.map((rect) => rect.x))
  const allMaxX = Math.max(...nodeRects.map((rect) => rect.x + rect.width))

  return new Map(
    data.swimlanes.map((swimlane) => {
      const ownRects = nodeRects.filter((rect) => rect.laneId === swimlane.id)

      return [
        swimlane.id,
        {
          ownMinY: Math.min(...ownRects.map((rect) => rect.y)),
          ownMaxY: Math.max(...ownRects.map((rect) => rect.y + rect.height)),
          allMinX,
          allMaxX,
        },
      ]
    })
  )
}

/** Finds the swimlane containing the given absolute point (the node's center), if any. */
export function findSwimlaneAt(
  absolutePoint: Point,
  swimlanes: SwimlaneSummary[]
): SwimlaneSummary | null {
  return (
    swimlanes.find((swimlane) => {
      const { x, y, width, height } = laneLayout(swimlane)

      return (
        absolutePoint.x >= x &&
        absolutePoint.x <= x + width &&
        absolutePoint.y >= y &&
        absolutePoint.y <= y + height
      )
    }) ?? null
  )
}

export interface Rect extends Point {
  width: number
  height: number
}

/** Finds the BPMN node whose rect contains the given absolute point (topmost by data order), if any. */
export function findNodeAt(
  absolutePoint: Point,
  data: CanvasData,
  nodeDimensions: (type: NodeDefinitionRender['type']) => { width: number; height: number }
): NodeDefinitionRender | null {
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))

  return (
    data.nodes.find((node) => {
      const lane = lanesById.get(node.swimlaneDefinitionId)
      if (!lane) {
        return false
      }
      const absolute = toAbsolutePosition(
        { x: node.layoutOptions?.x ?? 0, y: node.layoutOptions?.y ?? 0 },
        lane
      )
      const { width, height } = nodeDimensions(node.type)

      return (
        absolutePoint.x >= absolute.x &&
        absolutePoint.x <= absolute.x + width &&
        absolutePoint.y >= absolute.y &&
        absolutePoint.y <= absolute.y + height
      )
    }) ?? null
  )
}

/**
 * Palette drop validity: the dropped node's
 * rect (centered on the cursor) must sit fully inside the lane under the cursor,
 * and must not overlap an existing node. Same `overlapsAnyNode` rule a node drag
 * is held to, so a spot you can drag a node into is a spot you can drop one into.
 */
export function checkPaletteDropValidity(
  absolutePoint: Point,
  dimensions: { width: number; height: number },
  data: CanvasData,
  nodeDimensions: (type: NodeDefinitionRender['type']) => { width: number; height: number }
): { isInSwimlane: boolean; notOverlappingNodes: boolean } {
  const lane = findSwimlaneAt(absolutePoint, data.swimlanes)
  if (!lane) {
    return { isInSwimlane: false, notOverlappingNodes: true }
  }

  const layout = laneLayout(lane)
  const previewRect: Rect = {
    x: absolutePoint.x - dimensions.width / 2,
    y: absolutePoint.y - dimensions.height / 2,
    ...dimensions,
  }
  const isInSwimlane =
    previewRect.x >= layout.x &&
    previewRect.y >= layout.y &&
    previewRect.x + previewRect.width <= layout.x + layout.width &&
    previewRect.y + previewRect.height <= layout.y + layout.height

  // Nothing to exclude: the dropped node does not exist yet.
  const isOverlapping = overlapsAnyNode(previewRect, new Set(), data, nodeDimensions)

  return { isInSwimlane, notOverlappingNodes: !isOverlapping }
}

/**
 * A node is only valid when its rect sits
 * FULLY inside a single swimlane (straddling lanes or hanging outside is invalid).
 */
export function isRectInsideSomeLane(rect: Rect, swimlanes: SwimlaneSummary[]): boolean {
  return swimlanes.some((swimlane) => {
    const layout = laneLayout(swimlane)

    return (
      rect.x >= layout.x &&
      rect.y >= layout.y &&
      rect.x + rect.width <= layout.x + layout.width &&
      rect.y + rect.height <= layout.y + layout.height
    )
  })
}

/** Rect overlap against every other node. */
export function overlapsAnyNode(
  rect: Rect,
  excludedNodeIds: Set<NodeDefinitionRender['id']>,
  data: CanvasData,
  nodeDimensions: (type: NodeDefinitionRender['type']) => { width: number; height: number }
): boolean {
  const lanesById = new Map(data.swimlanes.map((swimlane) => [swimlane.id, swimlane]))

  return data.nodes
    .filter((node) => !excludedNodeIds.has(node.id))
    .some((node) => {
      const lane = lanesById.get(node.swimlaneDefinitionId)
      if (!lane) {
        return false
      }
      const absolute = toAbsolutePosition(
        { x: node.layoutOptions?.x ?? 0, y: node.layoutOptions?.y ?? 0 },
        lane
      )
      const { width, height } = nodeDimensions(node.type)

      return (
        rect.x < absolute.x + width &&
        rect.x + rect.width > absolute.x &&
        rect.y < absolute.y + height &&
        rect.y + rect.height > absolute.y
      )
    })
}

/**
 * Snaps a boundary event's node-relative position onto the border of its parent node,
 * since boundary events live on the node's edge.
 */
export function snapToNodeBorder(
  relativePosition: Point,
  parent: NodeDefinitionRender,
  boundarySize: { width: number; height: number }
): Point {
  const { width, height } = getNodeDimensions(parent.type)
  const halfW = boundarySize.width / 2
  const halfH = boundarySize.height / 2

  const centerX = Math.min(Math.max(relativePosition.x + halfW, 0), width)
  const centerY = Math.min(Math.max(relativePosition.y + halfH, 0), height)

  const distances = [
    { edge: 'left', value: centerX },
    { edge: 'right', value: width - centerX },
    { edge: 'top', value: centerY },
    { edge: 'bottom', value: height - centerY },
  ]
  const closest = distances.reduce((min, d) => (d.value < min.value ? d : min))

  const snappedCenter = {
    left: { x: 0, y: centerY },
    right: { x: width, y: centerY },
    top: { x: centerX, y: 0 },
    bottom: { x: centerX, y: height },
  }[closest.edge]

  return { x: snappedCenter.x - halfW, y: snappedCenter.y - halfH }
}
